export const calculateAge = (dob: string): number => {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

export const validateAgeForPassengerType = (
  dob: string,
  type: "ADT" | "CHD" | "INF",
): { isValid: boolean; message: string } => {
  if (!dob) return { isValid: false, message: "Date of birth is required" };

  const age = calculateAge(dob);

  switch (type) {
    case "ADT":
      return age >= 12
        ? { isValid: true, message: "" }
        : {
            isValid: false,
            message: `Age ${age} is too young for adult passenger (12+ required)`,
          };

    case "CHD":
      return age >= 2 && age <= 11
        ? { isValid: true, message: "" }
        : {
            isValid: false,
            message: `Age ${age} is invalid for child passenger (2-11 required)`,
          };

    case "INF":
      return age < 2
        ? { isValid: true, message: "" }
        : {
            isValid: false,
            message: `Age ${age} is too old for infant passenger (under 2 required)`,
          };

    default:
      return { isValid: false, message: "Invalid passenger type" };
  }
};

export const validatePassportExpiry = (
  expiryDate: string,
  departureDate?: string,
): { isValid: boolean; message: string } => {
  if (!expiryDate)
    return { isValid: false, message: "Passport expiry date is required" };

  const expiry = new Date(expiryDate);
  const today = new Date();

  // Check if passport is already expired
  if (expiry <= today) {
    return { isValid: false, message: "Passport has already expired" };
  }

  // Check if departure date is provided and passport expires within 1 month of departure
  if (departureDate) {
    const departure = new Date(departureDate);
    const oneMonthAfterDeparture = new Date(departure);
    oneMonthAfterDeparture.setMonth(oneMonthAfterDeparture.getMonth() + 1);

    if (expiry < oneMonthAfterDeparture) {
      return {
        isValid: false,
        message:
          "Passport must be valid for at least 1 month after departure date",
      };
    }
  }

  return { isValid: true, message: "" };
};

export const getMaxDateForPassengerType = (
  type: "ADT" | "CHD" | "INF",
): string => {
  const today = new Date();
  let maxDate = new Date(today);

  switch (type) {
    case "ADT":
      // 12 years ago
      maxDate.setFullYear(today.getFullYear() - 12);
      break;
    case "CHD":
      // 2 years ago (minimum age for child)
      maxDate.setFullYear(today.getFullYear() - 2);
      break;
    case "INF":
      // Today (infants can be newborns)
      break;
  }

  return maxDate.toISOString().split("T")[0];
};

export const getMinDateForPassengerType = (
  type: "ADT" | "CHD" | "INF",
): string => {
  const today = new Date();
  let minDate = new Date(today);

  switch (type) {
    case "ADT":
      // 100 years ago (reasonable limit for adults)
      minDate.setFullYear(today.getFullYear() - 100);
      break;
    case "CHD":
      // 12 years ago (maximum age for child)
      minDate.setFullYear(today.getFullYear() - 12);
      break;
    case "INF":
      // 2 years ago (maximum age for infant)
      minDate.setFullYear(today.getFullYear() - 2);
      break;
  }

  return minDate.toISOString().split("T")[0];
};

const TZ_OFFSET_TO_ABBR: Record<string, string> = {
  "+00:00": "UTC",
  "+01:00": "CET",
  "+02:00": "EET",
  "+03:00": "AST",
  "+03:30": "IRST",
  "+04:00": "GST",
  "+04:30": "AFT",
  "+05:00": "PKT",
  "+05:30": "IST",
  "+05:45": "NPT",
  "+06:00": "BST",
  "+06:30": "MMT",
  "+07:00": "ICT",
  "+08:00": "CST",
  "+09:00": "JST",
  "+09:30": "ACST",
  "+10:00": "AEST",
  "+11:00": "AEDT",
  "+12:00": "NZST",
  "-03:00": "ART",
  "-04:00": "EDT",
  "-05:00": "EST",
  "-06:00": "CST",
  "-07:00": "MST",
  "-08:00": "PST",
  "-09:00": "AKST",
  "-10:00": "HST",
};

export const getTimezoneAbbreviation = (dateTimeStr?: string): string => {
  if (!dateTimeStr) return "";
  if (/(?:T|^)\d{2}:\d{2}(?::\d{2})?Z$/.test(dateTimeStr)) return "UTC";
  const match = dateTimeStr.match(/([+-]\d{2}:\d{2})(?!.*[+-]\d{2}:\d{2})/);
  if (!match) return "";
  const offset = match[1];
  return TZ_OFFSET_TO_ABBR[offset] ?? `GMT${offset}`;
};

const offsetMinutes = (str: string): number | null => {
  if (/(?:T|^)\d{2}:\d{2}(?::\d{2})?Z$/.test(str)) return 0;
  const m = str.match(/([+-])(\d{2}):(\d{2})(?!.*[+-]\d{2}:\d{2})/);
  if (!m) return null;
  const sign = m[1] === "+" ? 1 : -1;
  return sign * (parseInt(m[2]) * 60 + parseInt(m[3]));
};

const localClockMinutes = (str: string): number | null => {
  const m = str.match(/(?:T|^)(\d{2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
};

export const computeDurationMinutes = (
  depDateTime?: string,
  arrDateTime?: string,
  arrDateAdjustment: number = 0,
): number => {
  if (!depDateTime || !arrDateTime) return 0;

  // Authoritative path: full ISO timestamps. Date.parse handles offsets, dates,
  // and DST automatically — trust the result, even if zero.
  const depDate = new Date(depDateTime);
  const arrDate = new Date(arrDateTime);
  if (!isNaN(depDate.getTime()) && !isNaN(arrDate.getTime())) {
    return Math.max(
      0,
      Math.round((arrDate.getTime() - depDate.getTime()) / 60000),
    );
  }

  // Fallback for time-only strings ("07:50:00+05:30" or "07:50:00").
  const depLocal = localClockMinutes(depDateTime);
  const arrLocal = localClockMinutes(arrDateTime);
  if (depLocal === null || arrLocal === null) return 0;

  const depOff = offsetMinutes(depDateTime);
  const arrOff = offsetMinutes(arrDateTime);
  // Apply TZ correction only when both offsets are known. If one is missing,
  // mixing UTC and local arithmetic produces a wrong number — assume same TZ.
  const tzDelta =
    depOff !== null && arrOff !== null ? depOff - arrOff : 0;
  return Math.max(
    0,
    arrLocal - depLocal + arrDateAdjustment * 1440 + tzDelta,
  );
};

/**
 * Format a date (with optional day adjustment) using the date portion of the
 * input string, NOT the browser's local timezone. Required because
 * `new Date(iso).getDate()` reflects whatever timezone the browser is in,
 * which can shift the displayed day by ±1 from the airport's local date.
 */
/**
 * "2026-09-15" (or any date `Date` can parse) → "15/09/2026".
 *
 * Reads the date portion straight out of an ISO string rather than through
 * `new Date(...)` first, so the answer is the same no matter which timezone the
 * browser is in — parsing "2026-09-15" as a Date is midnight UTC, which shows
 * up as the 14th in the Americas.
 */
export const toDDMMYYYY = (iso?: string | null): string => {
  if (!iso) return "";
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

/** ISO-ish datetime → "15/09/2026 10:30" in the browser's local time. */
export const toDDMMYYYYHHMM = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()} ${hh}:${mi}`;
};

export const formatLocalDateWithAdjustment = (
  baseStr?: string,
  adjustmentDays: number = 0,
): { date: string; day: string } => {
  if (!baseStr) return { date: "", day: "" };
  const m = baseStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { date: "", day: "" };
  const year = parseInt(m[1]);
  const month = parseInt(m[2]) - 1;
  const day = parseInt(m[3]);
  const target = new Date(Date.UTC(year, month, day + adjustmentDays));
  return {
    date: target.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }),
    day: target.toLocaleDateString("en-US", {
      weekday: "short",
      timeZone: "UTC",
    }),
  };
};
